import { HomeReview, ReviewResponse } from '../types';
import { dedupeResponsesByItemId } from './reviewResponseSelector';

export interface OfflineSyncReconciliationResponse {
  id: number;
  itemId?: number;
  syncStatus?: string;
  status?: string;
  updatedAt?: Date | string;
  lastLocalMutationAt?: Date | string | null;
  lastServerSeenAt?: Date | string | null;
  isPresent?: boolean;
  responseData?: unknown;
  notes?: string | null;
  roomId?: number | null;
  floor?: string | null;
  conflictStatus?: string | null;
}

export interface OfflineSyncReconciliationDatabase {
  getResponsesByReviewId(reviewId: number): Promise<OfflineSyncReconciliationResponse[]>;
  updateResponse(responseId: number, updates: {
    syncStatus?: 'pending' | 'synced' | 'failed';
    conflictStatus?: 'local_wins' | 'server_wins' | 'conflicted' | 'none';
    lastServerSeenAt?: Date | string;
  }): Promise<void>;
  markOfflineMutationsSyncedForResponse?(reviewId: number, itemId?: number | null): Promise<void>;
  recordOfflineMutation?(entry: {
    entityType: 'response';
    entityId?: number | null;
    reviewId?: number | null;
    itemId?: number | null;
    mutationType: string;
    payload?: unknown;
    previousSnapshot?: unknown;
    status?: 'pending' | 'synced' | 'failed' | 'conflict';
  }): Promise<number | undefined>;
  createRecoverySnapshot?(reviewId: number, reason: string): Promise<void>;
}

export interface OfflineSyncDiscrepancy {
  itemId: number;
  type: 'missing_on_server' | 'pending_sync' | 'client_newer' | 'server_newer' | 'server_completed' | 'data_mismatch';
  details?: string;
  winner?: 'local' | 'server';
}

export interface OfflineSyncReconciliationResult {
  needsSync: boolean;
  discrepancies: OfflineSyncDiscrepancy[];
}

const statusPriority: Record<string, number> = {
  not_started: 0,
  in_progress: 1,
  completed: 2,
};

const recoveryChoiceConflictStatuses = new Set(['local_wins', 'conflicted', 'restored']);
const activeSyncStatuses = new Set(['pending', 'syncing']);

const needsRecoveryChoice = (response: OfflineSyncReconciliationResponse): boolean =>
  response.conflictStatus === 'conflicted' ||
  (recoveryChoiceConflictStatuses.has(response.conflictStatus || '') &&
    !activeSyncStatuses.has(response.syncStatus || ''));

export class OfflineSyncReconciliationService {
  constructor(
    private readonly database: OfflineSyncReconciliationDatabase,
    private readonly triggerFullSync: () => Promise<void>
  ) {}

  async reconcileWithServer(
    serverReview: HomeReview,
    isOnline: boolean
  ): Promise<OfflineSyncReconciliationResult> {
    const discrepancies: OfflineSyncDiscrepancy[] = [];
    let needsSync = false;

    try {
      const offlineResponses = await this.database.getResponsesByReviewId(serverReview.id);
      if (offlineResponses.length === 0) {
        return { needsSync: false, discrepancies: [] };
      }

      const serverResponses = dedupeResponsesByItemId((serverReview.responses || []) as ReviewResponse[]);
      const serverResponseMap = new Map<number, ReviewResponse>(
        serverResponses.map((response): [number, ReviewResponse] => [response.itemId, response])
      );

      for (const offlineResponse of offlineResponses) {
        if (offlineResponse.itemId == null) {
          continue;
        }

        const serverResponse = serverResponseMap.get(offlineResponse.itemId);

        if (!serverResponse && needsRecoveryChoice(offlineResponse)) {
          continue;
        }

        if (!serverResponse && (offlineResponse.syncStatus === 'pending' || offlineResponse.syncStatus === 'failed')) {
          discrepancies.push({
            itemId: offlineResponse.itemId,
            type: 'pending_sync',
            details: `Response has syncStatus: ${offlineResponse.syncStatus}`,
            winner: 'local',
          });
          needsSync = true;
          continue;
        }

        if (!serverResponse && offlineResponse.conflictStatus === 'server_wins') {
          continue;
        }

        if (!serverResponse && offlineResponse.syncStatus === 'synced') {
          discrepancies.push({
            itemId: offlineResponse.itemId,
            type: 'missing_on_server',
            details: 'Response marked as synced but missing on server',
            winner: 'local',
          });
          await this.markLocalWinner(
            serverReview.id,
            offlineResponse,
            'missing_on_server',
            { serverResponse: null }
          );
          needsSync = true;
          continue;
        }

        if (serverResponse) {
          if (this.matchesServerResponse(offlineResponse, serverResponse)) {
            await this.database.updateResponse(offlineResponse.id, {
              syncStatus: 'synced',
              conflictStatus: 'none',
              lastServerSeenAt: serverResponse.updatedAt ? new Date(serverResponse.updatedAt) : new Date(),
            });
            await this.database.markOfflineMutationsSyncedForResponse?.(
              serverReview.id,
              offlineResponse.itemId
            );
            continue;
          }

          const winner = this.chooseWinner(offlineResponse, serverResponse);

          if (winner === 'local') {
            discrepancies.push({
              itemId: offlineResponse.itemId,
              type: this.getLocalWinnerType(offlineResponse, serverResponse),
              details: `Client has ${offlineResponse.status}, server has ${serverResponse.status}`,
              winner: 'local',
            });
            await this.markLocalWinner(
              serverReview.id,
              offlineResponse,
              'local_newer_or_more_complete',
              {
                serverResponseId: serverResponse.id,
                serverUpdatedAt: serverResponse.updatedAt,
                serverStatus: serverResponse.status,
              }
            );
            needsSync = true;
          } else if (winner === 'server') {
            await this.database.updateResponse(offlineResponse.id, {
              syncStatus: 'synced',
              conflictStatus: 'server_wins',
              lastServerSeenAt: serverResponse.updatedAt ? new Date(serverResponse.updatedAt) : new Date(),
            });
            discrepancies.push({
              itemId: offlineResponse.itemId,
              type: serverResponse.status === 'completed' && offlineResponse.status !== 'completed'
                ? 'server_completed'
                : 'server_newer',
              details: `Server kept for ${offlineResponse.status} -> ${serverResponse.status}`,
              winner: 'server',
            });
            await this.database.recordOfflineMutation?.({
              entityType: 'response',
              entityId: offlineResponse.id,
              reviewId: serverReview.id,
              itemId: offlineResponse.itemId,
              mutationType: 'reconciliation_server_winner',
              payload: {
                serverResponseId: serverResponse.id,
                serverUpdatedAt: serverResponse.updatedAt,
                serverStatus: serverResponse.status,
              },
              previousSnapshot: offlineResponse,
              status: 'synced',
            });
          }
        }
      }

      if (needsSync && isOnline) {
        console.warn('[OfflineSyncReconciliationService] Reconciliation found discrepancies:', {
          reviewId: serverReview.id,
          count: discrepancies.length,
          types: discrepancies.map(discrepancy => discrepancy.type),
        });
        this.triggerFullSync().catch(error => {
          console.error('[OfflineSyncReconciliationService] Reconciliation sync failed:', error);
        });
      }

      return { needsSync, discrepancies };
    } catch (error) {
      console.error('[OfflineSyncReconciliationService] Reconciliation failed:', error);
      return { needsSync: false, discrepancies: [] };
    }
  }

  private chooseWinner(
    offlineResponse: OfflineSyncReconciliationResponse,
    serverResponse: any
  ): 'local' | 'server' | 'same' {
    if (serverResponse.status === 'completed' && offlineResponse.status !== 'completed') {
      return 'server';
    }

    if (offlineResponse.conflictStatus === 'server_wins') {
      return 'server';
    }

    const localTimestamp = this.timestampMs(offlineResponse.lastLocalMutationAt || offlineResponse.updatedAt);
    const serverTimestamp = this.timestampMs(serverResponse.updatedAt);

    if (localTimestamp != null && serverTimestamp != null) {
      if (serverTimestamp > localTimestamp) {
        return 'server';
      }

      if (localTimestamp > serverTimestamp) {
        return 'local';
      }
    }

    if (
      offlineResponse.syncStatus === 'pending' ||
      offlineResponse.syncStatus === 'failed' ||
      offlineResponse.conflictStatus === 'local_wins' ||
      offlineResponse.conflictStatus === 'restored'
    ) {
      return 'local';
    }

    const clientPriority = statusPriority[offlineResponse.status as string] ?? 0;
    const serverPriority = statusPriority[serverResponse.status as string] ?? 0;
    if (clientPriority > serverPriority) {
      return 'local';
    }

    if (serverPriority > clientPriority) {
      return 'server';
    }

    return 'same';
  }

  private getLocalWinnerType(
    offlineResponse: OfflineSyncReconciliationResponse,
    serverResponse: any
  ): OfflineSyncDiscrepancy['type'] {
    const localTimestamp = this.timestampMs(offlineResponse.lastLocalMutationAt || offlineResponse.updatedAt);
    const serverTimestamp = this.timestampMs(serverResponse.updatedAt);
    if (localTimestamp != null && serverTimestamp != null && localTimestamp > serverTimestamp) {
      return 'client_newer';
    }

    return 'data_mismatch';
  }

  private async markLocalWinner(
    reviewId: number,
    offlineResponse: OfflineSyncReconciliationResponse,
    mutationType: string,
    payload: unknown
  ): Promise<void> {
    const isActiveLocalSync =
      offlineResponse.syncStatus === 'pending' || offlineResponse.syncStatus === 'syncing';
    await this.database.updateResponse(offlineResponse.id, {
      syncStatus: 'pending',
      conflictStatus: isActiveLocalSync ? 'none' : 'local_wins',
      lastServerSeenAt: new Date(),
    });
    if (!isActiveLocalSync) {
      await this.database.recordOfflineMutation?.({
        entityType: 'response',
        entityId: offlineResponse.id,
        reviewId,
        itemId: offlineResponse.itemId,
        mutationType: `reconciliation_${mutationType}`,
        payload,
        previousSnapshot: offlineResponse,
        status: 'pending',
      });
      await this.database.createRecoverySnapshot?.(reviewId, `reconciliation_${mutationType}`);
    }
    console.warn('[OfflineSyncReconciliationService] Local response wins reconciliation:', {
      reviewId,
      itemId: offlineResponse.itemId,
      localResponseId: offlineResponse.id,
      localUpdatedAt: offlineResponse.updatedAt,
      localMutationAt: offlineResponse.lastLocalMutationAt,
      localStatus: offlineResponse.status,
      mutationType,
      winner: 'local',
    });
  }

  private timestampMs(value: unknown): number | null {
    if (!value) {
      return null;
    }

    const time = new Date(value as any).getTime();
    return Number.isFinite(time) ? time : null;
  }

  private matchesServerResponse(
    offlineResponse: OfflineSyncReconciliationResponse,
    serverResponse: ReviewResponse
  ): boolean {
    return (
      offlineResponse.status === serverResponse.status &&
      Boolean(offlineResponse.isPresent) === Boolean(serverResponse.isPresent) &&
      this.normalizedText(offlineResponse.notes) === this.normalizedText((serverResponse as any).notes) &&
      this.stableJson(offlineResponse.responseData) === this.stableJson(serverResponse.responseData)
    );
  }

  private normalizedText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private stableJson(value: unknown): string {
    if (value == null) {
      return '';
    }

    return JSON.stringify(this.sortJson(value));
  }

  private sortJson(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(entry => this.sortJson(entry));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = this.sortJson((value as Record<string, unknown>)[key]);
          return acc;
        }, {});
    }

    return value;
  }
}
